// DecodedAudioStore - stores pre-decoded audio as Float32Array buffers
// Enables instant seeking by reading from stored buffers at any time position
//
// Usage:
//   const store = new DecodedAudioStore()
//   store.registerSource(uuid, { sampleRate: 48000, channels: 2 })
//   // During decode:
//   store.appendChunk(uuid, interleavedFloat32)
//   store.finalizeSource(uuid)
//   // During playback:
//   const samples = store.readAt(uuid, timeSeconds, frameCount)

import { resampleInterleaved } from './audioConvert'

// Phase A: stable identity for a Blob instance. Used as `contentTag` so a
// proxy hot swap (new blob under the SAME asset uuid) invalidates the stale
// decoded buffer instead of silently replaying the old audio.
const _blobTags = new WeakMap()
let _nextBlobTag = 1
export function getBlobContentTag(blob) {
  if (!blob || typeof blob !== 'object') return null
  let tag = _blobTags.get(blob)
  if (!tag) {
    tag = 'blob#' + _nextBlobTag++
    _blobTags.set(blob, tag)
  }
  return tag
}

export class DecodedAudioStore {
  constructor() {
    // Map<sourceKey, SourceEntry> (strip UUID legacy / asset UUID per-asset)
    this._sources = new Map()
    // Map<sourceKey, { promise, resolve, reject }> — waiters for in-flight
    // decodes (Phase A: a strip joining a shared source registers its
    // AudioPipeline window once the buffer finalizes).
    this._waiters = new Map()
    // Monotonic id stamped on each created source so releases can't be
    // misattributed across a drop-and-recreate under the same key.
    this._generationCounter = 0

    // Debug helper
    if (typeof window !== 'undefined') {
      window.vybyAudioStore = () => this
      // Phase 6.A diagnostic: per-strip audio path log. Captures every decision
      // point in the WebCodecs decode flow (gate, MP4 ready, codec accept/reject,
      // configure success/failure, first chunk arrival, fallback) so we can see
      // exactly where a clip drops out of the decoded path.
      window.__vybyAudioPathLog = window.__vybyAudioPathLog || {}
      window.__vybyDumpAudioPath = (uuid) => {
        if (!uuid) return window.__vybyAudioPathLog
        const events = window.__vybyAudioPathLog[uuid] || []
        if (!events.length) {
          // eslint-disable-next-line no-console
          console.info('[__vybyDumpAudioPath] no events for', uuid)
          return events
        }
        const t0 = events[0]?.ts || 0
        // eslint-disable-next-line no-console
        console.groupCollapsed(`[__vybyDumpAudioPath] ${uuid} (${events.length} events)`)
        events.forEach((ev) => {
          const dt = (ev.ts - t0).toFixed(1) + 'ms'
          // eslint-disable-next-line no-console
          console.log(dt.padStart(8), ev.key, ev.value)
        })
        // eslint-disable-next-line no-console
        console.groupEnd()
        return events
      }
      window.__vybyClearAudioPath = (uuid) => {
        if (uuid) delete window.__vybyAudioPathLog[uuid]
        else window.__vybyAudioPathLog = {}
      }
      // One-shot rate sanity probe: compares each registered source's stored
      // sampleRate against the AudioContext rate used by AudioPipeline. Useful for
      // verifying Phase 1/2 of the resample fix without printf-ing into hot paths.
      window.__vybyAudioRateCheck = () => {
        const out = []
        let ctxRate = null
        try {
          const pipe = typeof window.vybyAudioPipe === 'function' ? window.vybyAudioPipe() : null
          ctxRate = pipe?.audioCtx?.sampleRate ?? null
        } catch (e) { /* noop */ }
        this._sources.forEach((source, uuid) => {
          const stored = source.sampleRate
          const ratio = Number.isFinite(ctxRate) && stored ? (stored / ctxRate) : null
          out.push({
            uuid,
            stored,
            ctx: ctxRate,
            ratio,
            mismatch: Number.isFinite(ctxRate) ? Math.abs(stored - ctxRate) > 1 : null,
            ready: !!source.ready,
            durationSeconds: source.durationSeconds,
          })
        })
        return out
      }
    }
  }

  /**
   * Phase 6.A: append a diagnostic event to the per-strip audio path log.
   * Cheap noop when the log probe isn't initialized (test envs / SSR).
   * @param {string} uuid  Strip UUID
   * @param {string} key   Event key (e.g. 'gate', 'mp4Ready', 'firstChunk')
   * @param {object} value Arbitrary serializable details
   */
  recordPath(uuid, key, value) {
    if (!uuid || !key) return
    if (typeof window === 'undefined' || !window.__vybyAudioPathLog) return
    try {
      const log = window.__vybyAudioPathLog
      if (!log[uuid]) log[uuid] = []
      log[uuid].push({
        ts: performance.now?.() || Date.now(),
        key,
        value: value == null ? null : value,
      })
    } catch (e) { /* noop */ }
  }

  /**
   * Register a new source before appending chunks
   * @param {string} uuid - Source key (strip UUID legacy / asset UUID per-asset)
   * @param {object} opts - { sampleRate, channels }
   */
  registerSource(uuid, { sampleRate = 48000, channels = 2 } = {}) {
    if (!uuid) return
    if (this._sources.has(uuid)) {
      // Source already exists - reset it
      this.dropSource(uuid)
    }
    this._sources.set(uuid, {
      uuid,
      sampleRate: Number(sampleRate) || 48000,
      channels: Math.max(1, Number(channels) || 2),
      // Phase D Step 1: the rate the finalized buffer must end at (the
      // AudioContext rate). Chunks now arrive at the decoder's NATIVE rate
      // (sampleRate is updated via setSourceDecodeRate); finalizeSource does
      // ONE high-quality resample to targetSampleRate.
      targetSampleRate: Number(sampleRate) || 48000,
      chunks: [],
      totalSamples: 0, // Total interleaved samples (frames * channels)
      totalFrames: 0,
      ready: false,
      buffer: null, // Concatenated buffer after finalize
      durationSeconds: 0,
      createdAt: performance.now?.() || Date.now(),
      // Phase A: consumer refcount + content identity (see acquireSource).
      refCount: 0,
      contentTag: null,
    })
  }

  /**
   * Phase D Step 1: declare the actual decoder output rate for an in-flight
   * source. Chunks are stored at this NATIVE rate; finalizeSource resamples
   * the concatenated buffer to targetSampleRate in one pass (polyphase via
   * OfflineAudioContext, linear fallback). No-op once the source is ready.
   * @param {string} uuid - Source key
   * @param {number} rate - Decoder output sample rate (audioData.sampleRate)
   */
  setSourceDecodeRate(uuid, rate) {
    const source = this._sources.get(uuid)
    const r = Number(rate)
    if (!source || source.ready || !Number.isFinite(r) || r <= 0) return
    source.sampleRate = r
    source.durationSeconds = source.totalFrames / r
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Phase A — per-asset acquire/release (refcounted sources)
  //
  // Unlike registerSource (which drop-and-resets an existing key, correct for
  // the legacy one-strip-one-source model), acquireSource JOINS an existing or
  // in-flight source: two strips of the same asset share ONE decode and ONE
  // buffer. The source is dropped only when the LAST consumer releases it.
  //
  // `contentTag` identifies the underlying bytes (e.g. a per-blob key). The
  // proxy pipeline can hot-swap asset.blob mid-session under the SAME asset
  // uuid (meta.blobSource: 'proxy'), so acquiring with a different tag resets
  // the stale source and starts fresh.
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Acquire (create or join) a refcounted source.
   * @param {string} key - Source key (asset UUID)
   * @param {object} opts - { sampleRate, channels, contentTag }
   * @returns {{ joined: boolean, ready: boolean, refCount: number }|null}
   *   joined=true → a source for this key already existed (decode may be
   *   finished or still in flight); the caller must NOT start a second decode.
   */
  acquireSource(key, { sampleRate = 48000, channels = 2, contentTag = null } = {}) {
    if (!key) return null
    let source = this._sources.get(key)
    const tagChanged =
      source && contentTag != null && source.contentTag != null && source.contentTag !== contentTag
    if (tagChanged) {
      // Blob swapped under the same key (proxy hot swap) — stale audio, reset.
      this.dropSource(key)
      source = null
    }
    const joined = !!source
    if (!source) {
      this.registerSource(key, { sampleRate, channels })
      source = this._sources.get(key)
      source.generation = ++this._generationCounter
    }
    if (contentTag != null) source.contentTag = contentTag
    source.refCount = (source.refCount || 0) + 1
    return {
      joined,
      ready: !!source.ready,
      refCount: source.refCount,
      generation: source.generation,
    }
  }

  /**
   * Release a refcounted source. Drops the buffer when the last consumer
   * releases. Safe to call for unknown keys (no-op).
   * @param {string} key - Source key (asset UUID)
   * @param {number|null} generation - When provided, the release is skipped
   *   unless it matches the current source's generation. Protects against a
   *   strip that acquired an OLDER source under the same key (dropped and
   *   re-created since, e.g. proxy blob swap) decrementing the new source.
   * @returns {number} remaining refCount (0 when dropped/unknown)
   */
  releaseSource(key, generation = null) {
    const source = this._sources.get(key)
    if (!source) return 0
    if (generation != null && source.generation !== generation) return source.refCount || 0
    source.refCount = Math.max(0, (source.refCount || 0) - 1)
    if (source.refCount === 0) {
      this.dropSource(key)
      return 0
    }
    return source.refCount
  }

  /**
   * Current refcount for a source (0 for unknown keys).
   * @param {string} key - Source key
   * @returns {number}
   */
  getRefCount(key) {
    const source = this._sources.get(key)
    return source ? source.refCount || 0 : 0
  }

  /**
   * Promise that resolves when the source finalizes (immediately if already
   * ready) and rejects if the source is dropped first (decode error / blob
   * swap). Lets a strip that JOINED a shared in-flight decode register its
   * AudioPipeline window once the buffer lands.
   * @param {string} key - Source key
   * @returns {Promise<void>}
   */
  whenReady(key) {
    const source = this._sources.get(key)
    if (source && source.ready && source.buffer) return Promise.resolve()
    if (!source) return Promise.reject(new Error('source not registered: ' + key))
    let waiter = this._waiters.get(key)
    if (!waiter) {
      waiter = {}
      waiter.promise = new Promise((resolve, reject) => {
        waiter.resolve = resolve
        waiter.reject = reject
      })
      // Waiters can go unconsumed (e.g. source dropped with no joiners) —
      // pre-attach a noop catch so that never surfaces as an unhandled
      // rejection; each caller's own .catch still fires normally.
      waiter.promise.catch(() => {})
      this._waiters.set(key, waiter)
    }
    return waiter.promise
  }

  _settleWaiter(key, error = null) {
    const waiter = this._waiters.get(key)
    if (!waiter) return
    this._waiters.delete(key)
    if (error) waiter.reject(error)
    else waiter.resolve()
  }

  /**
   * Append decoded audio chunk to a source
   * @param {string} uuid - Strip UUID
   * @param {Float32Array} interleaved - Interleaved audio samples
   */
  appendChunk(uuid, interleaved) {
    const source = this._sources.get(uuid)
    if (!source) {
      console.warn('[DecodedAudioStore] appendChunk: source not registered', uuid)
      return
    }
    if (source.ready) {
      console.warn('[DecodedAudioStore] appendChunk: source already finalized', uuid)
      return
    }
    if (!(interleaved instanceof Float32Array) || !interleaved.length) {
      return
    }

    // Store a copy to avoid holding references to decoded AudioData buffers
    const copy = new Float32Array(interleaved.length)
    copy.set(interleaved)
    source.chunks.push(copy)
    source.totalSamples += copy.length
    source.totalFrames = Math.floor(source.totalSamples / source.channels)
    source.durationSeconds = source.totalFrames / source.sampleRate
  }

  /**
   * Mark source as complete: concatenate chunks into a single buffer and,
   * when the chunks were stored at a native rate different from the target
   * (AudioContext) rate, resample the WHOLE buffer in one pass — polyphase
   * via OfflineAudioContext (Phase D Step 1 / §7 Option A), linear-interp
   * fallback when it's unavailable. Readiness (`ready`, `whenReady`) is only
   * signalled once the buffer is at the target rate.
   * @param {string} uuid - Source key
   * @returns {Promise<void>} resolves when the source is ready (or was
   *   dropped mid-finalize — callers re-check isReady before registering)
   */
  finalizeSource(uuid) {
    const source = this._sources.get(uuid)
    if (!source) return Promise.resolve()
    if (source.ready) return Promise.resolve()
    if (source._finalizing) return source._finalizing

    // Concatenate all chunks into single buffer for efficient random access
    if (source.chunks.length === 0) {
      source.buffer = new Float32Array(0)
    } else if (source.chunks.length === 1) {
      source.buffer = source.chunks[0]
    } else {
      source.buffer = new Float32Array(source.totalSamples)
      let offset = 0
      for (const chunk of source.chunks) {
        source.buffer.set(chunk, offset)
        offset += chunk.length
      }
    }

    // Clear chunks array to free memory (buffer holds all data now)
    source.chunks = []

    const target = Number(source.targetSampleRate) || source.sampleRate
    if (!source.buffer.length || Math.abs(source.sampleRate - target) <= 1) {
      this._markSourceReady(source)
      return Promise.resolve()
    }

    // Native-rate buffer → one high-quality resample to the target rate.
    // The generation guard abandons the result if the source was dropped or
    // re-created (proxy blob swap, teardown) while rendering.
    const generation = source.generation
    const nativeRate = source.sampleRate
    source._finalizing = this._resampleBuffer(source.buffer, source.channels, nativeRate, target)
      .then((resampled) => {
        source._finalizing = null
        const current = this._sources.get(uuid)
        if (current !== source || current.generation !== generation) return
        source.buffer = resampled
        source.sampleRate = target
        source.totalSamples = resampled.length
        source.totalFrames = Math.floor(resampled.length / source.channels)
        source.durationSeconds = source.totalFrames / target
        this._markSourceReady(source, { resampledFrom: nativeRate })
      })
      .catch((err) => {
        // Both resample paths failing is effectively impossible (the linear
        // fallback is pure JS) — but never leave a source hanging: signal
        // ready with the native-rate buffer (readers use source.sampleRate).
        source._finalizing = null
        const current = this._sources.get(uuid)
        if (current !== source || current.generation !== generation) return
        console.warn('[DecodedAudioStore] finalize resample failed; keeping native rate', err)
        this._markSourceReady(source)
      })
    return source._finalizing
  }

  _markSourceReady(source, { resampledFrom = null } = {}) {
    source.ready = true
    source.finalizedAt = performance.now?.() || Date.now()
    this._settleWaiter(source.uuid)

    if (typeof window !== 'undefined' && window.vybyTimingDebug) {
      console.info('[DecodedAudioStore] finalized', {
        uuid: source.uuid,
        frames: source.totalFrames,
        duration: source.durationSeconds.toFixed(2) + 's',
        sampleRate: source.sampleRate,
        resampledFrom,
        channels: source.channels,
        memoryMB: ((source.buffer.byteLength) / (1024 * 1024)).toFixed(2),
      })
    }
  }

  // Phase D Step 1: browser-native polyphase resample of a full interleaved
  // buffer via OfflineAudioContext.startRendering() (runs off the main
  // thread). Falls back to the JS linear-interp resampler when
  // OfflineAudioContext is unavailable or rendering fails.
  async _resampleBuffer(interleaved, channels, srcRate, dstRate) {
    const OAC = (typeof window !== 'undefined' &&
      (window.OfflineAudioContext || window.webkitOfflineAudioContext)) || null
    if (OAC) {
      try {
        const ch = Math.max(1, channels | 0)
        const frames = Math.floor(interleaved.length / ch)
        const outFrames = Math.max(1, Math.ceil(frames * dstRate / srcRate))
        const octx = new OAC(ch, outFrames, dstRate)
        const audioBuf = octx.createBuffer(ch, frames, srcRate)
        for (let c = 0; c < ch; c++) {
          const plane = audioBuf.getChannelData(c)
          for (let f = 0; f < frames; f++) plane[f] = interleaved[f * ch + c]
        }
        const node = octx.createBufferSource()
        node.buffer = audioBuf
        node.connect(octx.destination)
        node.start(0)
        const rendered = await octx.startRendering()
        const renderedFrames = rendered.length
        const out = new Float32Array(renderedFrames * ch)
        for (let c = 0; c < ch; c++) {
          const plane = rendered.getChannelData(Math.min(c, rendered.numberOfChannels - 1))
          for (let f = 0; f < renderedFrames; f++) out[f * ch + c] = plane[f]
        }
        return out
      } catch (e) {
        // fall through to the linear fallback
      }
    }
    return resampleInterleaved(interleaved, channels, srcRate, dstRate)
  }

  /**
   * Check if source is ready for playback
   * @param {string} uuid - Strip UUID
   * @returns {boolean}
   */
  isReady(uuid) {
    const source = this._sources.get(uuid)
    return !!(source && source.ready && source.buffer)
  }

  /**
   * Check if source exists (registered but may not be ready)
   * @param {string} uuid - Strip UUID
   * @returns {boolean}
   */
  hasSource(uuid) {
    return this._sources.has(uuid)
  }

  /**
   * Get source metadata
   * @param {string} uuid - Strip UUID
   * @returns {object|null}
   */
  getInfo(uuid) {
    const source = this._sources.get(uuid)
    if (!source) return null
    return {
      uuid: source.uuid,
      sampleRate: source.sampleRate,
      channels: source.channels,
      totalFrames: source.totalFrames,
      durationSeconds: source.durationSeconds,
      ready: source.ready,
      memoryBytes: source.buffer ? source.buffer.byteLength : 0,
      refCount: source.refCount || 0,
    }
  }

  /**
   * Read interleaved samples at a given time position
   * @param {string} uuid - Strip UUID
   * @param {number} timeSeconds - Time position in seconds
   * @param {number} frameCount - Number of frames to read
   * @returns {Float32Array|null} - Interleaved samples or null if not ready
   */
  readAt(uuid, timeSeconds, frameCount) {
    const source = this._sources.get(uuid)
    if (!source || !source.ready || !source.buffer) return null

    const ch = source.channels
    const sr = source.sampleRate
    const totalFrames = source.totalFrames

    // Convert time to frame index
    const startFrame = Math.max(0, Math.floor(timeSeconds * sr))
    const requestedFrames = Math.max(0, Math.floor(frameCount))

    if (startFrame >= totalFrames) {
      // Past end of audio - return silence
      return new Float32Array(requestedFrames * ch)
    }

    // Clamp to available frames
    const availableFrames = totalFrames - startFrame
    const framesToRead = Math.min(requestedFrames, availableFrames)

    // Calculate sample indices
    const startSample = startFrame * ch
    const endSample = startSample + framesToRead * ch

    // Extract slice from buffer
    const slice = source.buffer.subarray(startSample, endSample)

    // If we need more frames than available, pad with silence
    if (framesToRead < requestedFrames) {
      const output = new Float32Array(requestedFrames * ch)
      output.set(slice)
      // Rest is already zeros (Float32Array default)
      return output
    }

    // Return a copy to avoid external mutation
    const output = new Float32Array(slice.length)
    output.set(slice)
    return output
  }

  /**
   * Varispeed read: linear-interpolated interleaved samples starting at a
   * FRACTIONAL frame position, advancing a FRACTIONAL step per output frame.
   * step > 1 consumes source faster (speed-up), < 1 slower (slow-mo). Out of
   * range positions read as silence. Used by the retime-aware mixers.
   * @param {string} uuid - Strip UUID
   * @param {number} startFramePos - Starting source frame (fractional ok)
   * @param {number} stepFrames - Source frames consumed per output frame
   * @param {number} frameCount - Number of output frames
   * @returns {Float32Array|null} - Interleaved samples or null if not ready
   */
  readInterp(uuid, startFramePos, stepFrames, frameCount) {
    const source = this._sources.get(uuid)
    if (!source || !source.ready || !source.buffer) return null

    const ch = source.channels
    const totalFrames = source.totalFrames
    const buffer = source.buffer
    const count = Math.max(0, Math.floor(frameCount))
    const out = new Float32Array(count * ch)
    let pos = Number(startFramePos) || 0
    const step = Number(stepFrames) || 0
    for (let i = 0; i < count; i++) {
      const f0 = Math.floor(pos)
      const frac = pos - f0
      const f1 = f0 + 1
      const in0 = f0 >= 0 && f0 < totalFrames
      const in1 = f1 >= 0 && f1 < totalFrames
      const base0 = f0 * ch
      const base1 = f1 * ch
      const outBase = i * ch
      for (let c = 0; c < ch; c++) {
        const a = in0 ? buffer[base0 + c] : 0
        const b = in1 ? buffer[base1 + c] : 0
        out[outBase + c] = a + (b - a) * frac
      }
      pos += step
    }
    return out
  }

  /**
   * Read interleaved samples at a given frame position (more efficient than time-based)
   * @param {string} uuid - Strip UUID
   * @param {number} startFrame - Starting frame index
   * @param {number} frameCount - Number of frames to read
   * @returns {Float32Array|null} - Interleaved samples or null if not ready
   */
  readFrames(uuid, startFrame, frameCount) {
    const source = this._sources.get(uuid)
    if (!source || !source.ready || !source.buffer) return null

    const ch = source.channels
    const totalFrames = source.totalFrames

    const start = Math.max(0, Math.floor(startFrame))
    const requested = Math.max(0, Math.floor(frameCount))

    if (start >= totalFrames) {
      return new Float32Array(requested * ch)
    }

    const available = totalFrames - start
    const toRead = Math.min(requested, available)

    const startSample = start * ch
    const endSample = startSample + toRead * ch
    const slice = source.buffer.subarray(startSample, endSample)

    if (toRead < requested) {
      const output = new Float32Array(requested * ch)
      output.set(slice)
      return output
    }

    const output = new Float32Array(slice.length)
    output.set(slice)
    return output
  }

  /**
   * Get the duration of a source in seconds
   * @param {string} uuid - Strip UUID
   * @returns {number}
   */
  getDuration(uuid) {
    const source = this._sources.get(uuid)
    return source ? source.durationSeconds : 0
  }

  /**
   * Get the sample rate of a source
   * @param {string} uuid - Strip UUID
   * @returns {number}
   */
  getSampleRate(uuid) {
    const source = this._sources.get(uuid)
    return source ? source.sampleRate : 48000
  }

  /**
   * Get the channel count of a source
   * @param {string} uuid - Strip UUID
   * @returns {number}
   */
  getChannels(uuid) {
    const source = this._sources.get(uuid)
    return source ? source.channels : 2
  }

  /**
   * Remove a source and free memory
   * @param {string} uuid - Strip UUID
   */
  dropSource(uuid) {
    const source = this._sources.get(uuid)
    if (!source) return

    // Clear references to allow GC
    source.chunks = []
    source.buffer = null
    this._sources.delete(uuid)
    this._settleWaiter(uuid, new Error('source dropped: ' + uuid))
  }

  /**
   * Clear all sources
   */
  clear() {
    this._sources.forEach((source, uuid) => {
      source.chunks = []
      source.buffer = null
      this._settleWaiter(uuid, new Error('store cleared'))
    })
    this._sources.clear()
  }

  /**
   * Get debug info for all sources
   * @returns {object}
   */
  debugInfo() {
    const sources = {}
    this._sources.forEach((source, uuid) => {
      sources[uuid] = {
        ready: source.ready,
        finalizing: !!source._finalizing,
        frames: source.totalFrames,
        duration: source.durationSeconds,
        sampleRate: source.sampleRate,
        targetSampleRate: source.targetSampleRate,
        channels: source.channels,
        memoryMB: source.buffer
          ? (source.buffer.byteLength / (1024 * 1024)).toFixed(2)
          : 0,
        chunksCount: source.chunks.length,
        refCount: source.refCount || 0,
        contentTag: source.contentTag || null,
      }
    })
    return {
      sourceCount: this._sources.size,
      sources,
    }
  }
}

// Singleton instance for editor-wide use
let _instance = null

export function getDecodedAudioStore() {
  if (!_instance) {
    _instance = new DecodedAudioStore()
  }
  return _instance
}

export default DecodedAudioStore

